import { useCallback, useRef, useState } from "react";

export function usePanZoom(initial = { yaw: 0, pitch: 0, fov: 75 }) {
  const [view, setView] = useState(initial);
  const dragging = useRef(false);
  const last = useRef(null);
  const velocity = useRef({ x: 0, y: 0 });
  const animationFrame = useRef(null);
  
  // Improved sensitivity settings
  const SENSITIVITY = {
    yaw: 0.15,      // Horizontal rotation speed
    pitch: 0.15,    // Vertical rotation speed
    wheel: 3,       // Zoom speed
    smoothing: 0.8  // Smoothing factor for velocity
  };
  
  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    velocity.current = { x: 0, y: 0 };
    
    // Cancel any ongoing animation
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  }, []);
  
  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !last.current) return;
    
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    
    // Calculate velocity for smoothing
    velocity.current = {
      x: dx * SENSITIVITY.yaw,
      y: dy * SENSITIVITY.pitch
    };
    
    last.current = { x: e.clientX, y: e.clientY };
    
    setView(v => {
      const newYaw = v.yaw - velocity.current.x; // Inverted for natural feel - mouse right = look right
      const newPitch = Math.max(-89, Math.min(89, v.pitch - velocity.current.y)); // Inverted for natural feel
      
      return {
        ...v,
        yaw: newYaw,
        pitch: newPitch
      };
    });
  }, []);
  
  const onMouseUp = useCallback(() => {
    dragging.current = false;
    last.current = null;
    
    // Add momentum/inertia for smoother feel
    if (Math.abs(velocity.current.x) > 0.1 || Math.abs(velocity.current.y) > 0.1) {
      const applyMomentum = () => {
        if (Math.abs(velocity.current.x) < 0.01 && Math.abs(velocity.current.y) < 0.01) {
          velocity.current = { x: 0, y: 0 };
          return;
        }
        
        setView(v => {
          const newYaw = v.yaw - velocity.current.x; // Inverted for natural feel - mouse right = look right
          const newPitch = Math.max(-89, Math.min(89, v.pitch - velocity.current.y));
          
          return {
            ...v,
            yaw: newYaw,
            pitch: newPitch
          };
        });
        
        // Apply friction
        velocity.current = {
          x: velocity.current.x * SENSITIVITY.smoothing,
          y: velocity.current.y * SENSITIVITY.smoothing
        };
        
        animationFrame.current = requestAnimationFrame(applyMomentum);
      };
      
      animationFrame.current = requestAnimationFrame(applyMomentum);
    }
  }, []);
  
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? SENSITIVITY.wheel : -SENSITIVITY.wheel;
    
    setView(v => ({
      ...v,
      fov: Math.max(20, Math.min(120, v.fov + zoomDelta))
    }));
  }, []);
  
  const bind = useCallback((el) => {
    if (!el) return;
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("wheel", onWheel);
      
      // Cancel any ongoing animation
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
        animationFrame.current = null;
      }
    };
  }, [onMouseDown, onMouseMove, onMouseUp, onWheel]);
  
  return { view, setView, bind };
}
